'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ReactNode, createContext, useMemo } from 'react';
import { getFormattedVenueOpeningHours } from '@/entities/venue/lib/get-formatted-venue-opening-hours.util';
import {
    getVenueAvailabilityStatus,
    VenueAvailabilityKind,
} from '@/entities/venue/lib/venue-availability.utils';
import { useWebsiteConfig } from '@/features/app-providers/website-config-provider';
import { CompleteVenue, VenuesQuery } from '@/features/venue-router';
import { useIsVenueRouterPage } from '../hooks/useIsVenueRouterPage';

export type VenuesFilters = {
    onlyOpen?: boolean;
    limit?: number;
};

export const VenuesContext = createContext<{ data: CompleteVenue[] }>({ data: [] });

type Props = {
    filter?: VenuesFilters;
    children?: ReactNode;
};

export const VenuesProvider = ({ filter, children }: Props) => {
    const t = useTranslations();
    const isVenueRouterPage = useIsVenueRouterPage();
    const { data: venues = { venues: [], mappedVenues: {} } } = useQuery({
        ...VenuesQuery,
        select: (data) => ({
            venues: data,
            mappedVenues: Object.fromEntries(data.map((venue) => [venue.apiPartnerSettingsId, venue])),
        }),
    });
    const website = useWebsiteConfig();
    const allVenues = useMemo(
        () => [
            ...(website.venueRouter?.branches ?? []).map(
                (branch) => venues.mappedVenues[branch.apiPartnerSettingsId],
            ),
            ...venues.venues,
        ],
        [venues.mappedVenues, venues.venues, website.venueRouter?.branches],
    );

    const data = useMemo(() => {
        const sortedIds = new Set<string>();
        const orderedVenues: CompleteVenue[] = [];

        for (const venue of allVenues) {
            if (typeof filter?.limit === 'number' && orderedVenues.length === filter.limit) {
                break;
            }
            if (!venue || sortedIds.has(venue.apiPartnerSettingsId)) {
                continue;
            }

            sortedIds.add(venue.apiPartnerSettingsId);

            if (!isVenueRouterPage && venue.isCurrent) {
                continue;
            }
            if (filter?.onlyOpen) {
                const status = getVenueAvailabilityStatus({
                    openingHours: getFormattedVenueOpeningHours(venue.openingHours ?? [], t),
                    outages: [],
                });
                if (status.kind !== VenueAvailabilityKind.Open) {
                    continue;
                }
            }

            orderedVenues.push(venue);
        }

        return orderedVenues;
    }, [t, isVenueRouterPage, filter, allVenues]);

    return <VenuesContext.Provider value={{ data }}>{children}</VenuesContext.Provider>;
};
